/*
 * dg-runtime.js — Dropguys replacement for the Instant page-builder runtime.
 * Drives the components used by the (former) Instant sections in this theme:
 *   sliders (reads the Swiper params the sections already ship), tickers,
 *   accordions, fill-video play/pause, scroll-reveal, clickable containers.
 * No Instant scripts or assets are loaded and no external slider library is
 * needed: the slider is a small Swiper-compatible implementation.
 */
(function () {
  'use strict';
  function debounce(fn, ms) {
    var t; return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  /* ---------- Sliders: lightweight Swiper-compatible slider ----------
     Reads the same params JSON the sections ship for Swiper and honours the
     subset they use: breakpoints, slidesPerView, spaceBetween, centeredSlides,
     loop, speed, autoplay (delay / pauseOnMouseEnter / disableOnInteraction),
     navigation prev/next buttons, touch/drag. Uses Swiper's class contract
     (instant-slider-*), so the existing section CSS applies unchanged. */
  function DGSlider(el, params) {
    var self = this;
    this.el = el; this.params = params || {};
    var p = this.params;
    this.wrapper = el.querySelector(':scope > .' + (p.wrapperClass || 'instant-slider-wrapper')) || el.querySelector('.instant-slider-wrapper');
    if (!this.wrapper) return;
    this.slideClass = p.slideClass || 'instant-slider-slide';
    this.activeClass = p.slideActiveClass || 'instant-slider-slide-active';
    this.visibleClass = p.slideVisibleClass || 'instant-slider-slide-visible';
    this.originals = Array.prototype.slice.call(this.wrapper.children).filter(function (c) { return c.classList.contains(self.slideClass); });
    this.loop = !!p.loop && this.originals.length > 1;
    this.index = 0; this.timer = null; this.busy = false; this.interacted = false;

    el.classList.add((p.containerModifierClass || 'instant-slider-') + 'horizontal');
    if (this.loop) {
      var n = this.originals.length;
      for (var i = 0; i < n; i++) { var c = this.originals[i].cloneNode(true); c.setAttribute('data-dg-clone', '1'); this.wrapper.appendChild(c); }
      for (var j = n - 1; j >= 0; j--) { var c2 = this.originals[j].cloneNode(true); c2.setAttribute('data-dg-clone', '1'); this.wrapper.insertBefore(c2, this.wrapper.firstChild); }
      this.index = n;
    }
    this.slides = Array.prototype.slice.call(this.wrapper.children).filter(function (c) { return c.classList.contains(self.slideClass); });

    this.resolve = function () {
      var s = { slidesPerView: 1, spaceBetween: 0, centeredSlides: false, speed: 300 };
      ['slidesPerView', 'spaceBetween', 'centeredSlides', 'speed', 'autoplay'].forEach(function (k) { if (p[k] !== undefined) s[k] = p[k]; });
      if (p.breakpoints) {
        var w = window.innerWidth;
        Object.keys(p.breakpoints).map(Number).sort(function (a, b) { return a - b; }).forEach(function (bp) {
          if (w >= bp) { var b = p.breakpoints[bp]; ['slidesPerView', 'spaceBetween', 'centeredSlides', 'speed', 'autoplay'].forEach(function (k) { if (b[k] !== undefined) s[k] = b[k]; }); }
        });
      }
      if (s.slidesPerView === 'auto' || !(s.slidesPerView > 0)) s.slidesPerView = 1;
      return s;
    };

    this.layout = function () {
      var s = self.s = self.resolve();
      var cw = el.clientWidth;
      var sw = (cw - (s.slidesPerView - 1) * s.spaceBetween) / s.slidesPerView;
      self.slideWidth = sw; self.step = sw + s.spaceBetween;
      self.slides.forEach(function (sl) { sl.style.width = sw + 'px'; sl.style.marginRight = s.spaceBetween + 'px'; });
      self.translate(false);
    };

    this.offsetFor = function (idx) {
      var cw = el.clientWidth;
      var base = -idx * self.step;
      if (self.s.centeredSlides) base += (cw - self.slideWidth) / 2;
      return base;
    };

    this.translate = function (animate) {
      self.wrapper.style.transitionDuration = (animate ? self.s.speed : 0) + 'ms';
      self.wrapper.style.transform = 'translate3d(' + self.offsetFor(self.index) + 'px,0,0)';
      self.slides.forEach(function (sl, i) {
        sl.classList.toggle(self.activeClass, i === self.index);
        sl.classList.toggle(self.visibleClass, i >= self.index && i < self.index + Math.ceil(self.s.slidesPerView));
      });
      self.updateNav();
    };

    this.maxIndex = function () { return Math.max(0, self.slides.length - Math.ceil(self.s.slidesPerView)); };

    this.slideTo = function (idx, animate) {
      if (self.busy) return;
      if (!self.loop) idx = Math.max(0, Math.min(idx, self.maxIndex()));
      self.index = idx; self.busy = animate !== false;
      self.translate(animate !== false);
      if (self.busy) setTimeout(self.afterTransition, self.s.speed + 20);
    };
    this.afterTransition = function () {
      self.busy = false;
      if (self.loop) {
        var n = self.originals.length;
        if (self.index >= 2 * n) { self.index -= n; self.translate(false); }
        else if (self.index < n) { self.index += n; self.translate(false); }
      }
    };
    this.next = function () { self.slideTo(self.index + 1); };
    this.prev = function () { self.slideTo(self.index - 1); };

    /* navigation */
    this.nav = p.navigation && typeof p.navigation === 'object' ? p.navigation : null;
    this.updateNav = function () {
      if (!self.nav) return;
      var dis = self.nav.disabledClass || 'instant-slider-button-disabled';
      if (self.nav.prevEl) self.nav.prevEl.classList.toggle(dis, !self.loop && self.index <= 0);
      if (self.nav.nextEl) self.nav.nextEl.classList.toggle(dis, !self.loop && self.index >= self.maxIndex());
    };
    if (this.nav) {
      if (this.nav.prevEl) this.nav.prevEl.addEventListener('click', function (e) { e.preventDefault(); self.interacted = true; self.prev(); });
      if (this.nav.nextEl) this.nav.nextEl.addEventListener('click', function (e) { e.preventDefault(); self.interacted = true; self.next(); });
    }

    /* autoplay */
    this.startAutoplay = function () {
      var a = self.s.autoplay;
      if (!a || a.enabled === false) return;
      var delay = (typeof a === 'object' && a.delay) || 3000;
      self.stopAutoplay();
      self.timer = setInterval(function () { if (!self.busy) self.slideTo(self.index + 1); }, delay);
    };
    this.stopAutoplay = function () { if (self.timer) { clearInterval(self.timer); self.timer = null; } };
    el.addEventListener('mouseenter', function () { var a = self.s.autoplay; if (a && a.pauseOnMouseEnter) self.stopAutoplay(); });
    el.addEventListener('mouseleave', function () { var a = self.s.autoplay; if (a && a.pauseOnMouseEnter && !(a.disableOnInteraction && self.interacted)) self.startAutoplay(); });

    /* touch / drag */
    var startX = 0, startT = 0, dragging = false, startOffset = 0;
    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true; startX = e.clientX; startT = Date.now(); startOffset = self.offsetFor(self.index);
      self.stopAutoplay(); self.wrapper.style.transitionDuration = '0ms';
    });
    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 5) { self.wrapper.style.transform = 'translate3d(' + (startOffset + dx) + 'px,0,0)'; }
    });
    function endDrag(e) {
      if (!dragging) return; dragging = false;
      var dx = e.clientX - startX, dt = Date.now() - startT;
      var threshold = Math.min(self.slideWidth * 0.2, 60);
      self.interacted = true;
      if (dx < -threshold || (dx < -10 && dt < 300)) self.slideTo(self.index + 1);
      else if (dx > threshold || (dx > 10 && dt < 300)) self.slideTo(self.index - 1);
      else self.translate(true);
      var a = self.s.autoplay; if (a && !a.disableOnInteraction) self.startAutoplay();
    }
    window.addEventListener('pointerup', endDrag); window.addEventListener('pointercancel', endDrag);
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
    el.querySelectorAll('img').forEach(function (img) { img.setAttribute('draggable', 'false'); });

    this.layout();
    var relayout = debounce(function () { self.layout(); }, 100);
    if ('ResizeObserver' in window) new ResizeObserver(relayout).observe(el); else window.addEventListener('resize', relayout);
    window.addEventListener('load', relayout);
    this.startAutoplay();
    el.classList.add((p.containerModifierClass || 'instant-slider-') + 'initialized');
    el.setAttribute('data-instant-slider-initialized', 'true');
    el.dgSlider = this;
  }

  function initSliders(root) {
    root.querySelectorAll('[data-instant-type="slider"]:not([data-instant-slider-initialized])').forEach(function (el) {
      var params = {};
      var paramsEl = document.getElementById('instant-slider-' + el.id + '-params');
      try { params = JSON.parse(paramsEl ? paramsEl.textContent : '{}'); } catch (e) { params = {}; }
      var container = el.closest('[data-instant-type="slider-container"]') || el.parentElement;
      var section = el.closest('.__instant') || document;
      if (params.navigation) {
        var nav = typeof params.navigation === 'object' ? params.navigation : {};
        var prevEl = (nav.prevEl && section.querySelector(nav.prevEl)) || container.querySelector('[data-instant-type="slider-button-prev"]');
        var nextEl = (nav.nextEl && section.querySelector(nav.nextEl)) || container.querySelector('[data-instant-type="slider-button-next"]');
        params.navigation = (prevEl || nextEl) ? Object.assign({}, nav, { prevEl: prevEl, nextEl: nextEl }) : false;
      }
      try { new DGSlider(el, params); } catch (e) { console.warn('dg-runtime: slider init failed', e); }
    });
  }

  /* ---------- Tickers (marquee) ---------- */
  function initTickers(root) {
    root.querySelectorAll('[data-instant-type="ticker"]').forEach(function (n) {
      if (n.getAttribute('data-dg-ticker')) return;
      n.setAttribute('data-dg-ticker', '1');
      var initial = n.querySelector('.instant-ticker-initial-child-container');
      if (!initial) return;
      var first = initial.closest('.instant-ticker');
      var second = first ? first.nextElementSibling : null;
      if (!first) return;
      var reversed = n.getAttribute('data-instant-ticker-is-reversed') === 'true';
      var speed = +(n.getAttribute('data-instant-ticker-speed') || 50);
      var pause = n.getAttribute('data-instant-ticker-pause') === 'true';
      var originals = Array.prototype.slice.call(initial.children);

      function build() {
        var cw = n.getBoundingClientRect().width;
        var iw = initial.getBoundingClientRect().width;
        if (!cw || !iw) return;
        var gap = parseFloat(getComputedStyle(first).columnGap) || 0;
        var mult = Math.max(1, Math.ceil(cw / (iw + gap)) + 1);
        [first, second].forEach(function (track, idx) {
          if (!track) return;
          Array.prototype.slice.call(track.children).forEach(function (c) { if (c !== initial) c.remove(); });
          var copies = idx === 0 ? mult - 1 : mult;
          for (var i = 0; i < copies; i++) originals.forEach(function (c) { track.appendChild(c.cloneNode(true)); });
        });
        var total = first.getBoundingClientRect().width;
        n.style.setProperty('--gap', gap + 'px');
        n.style.setProperty('--duration', (total / speed) + 's');
        n.style.setProperty('--direction', reversed ? 'reverse' : 'normal');
        n.style.setProperty('--play', 'running');
        n.style.setProperty('--pause-on-hover', pause ? 'paused' : 'running');
        n.style.setProperty('--pause-on-click', pause ? 'paused' : 'running');
        n.style.setProperty('--multiplier', String(mult));
      }
      var rebuild = debounce(build, 150);
      build();
      if ('ResizeObserver' in window) { var ro = new ResizeObserver(rebuild); ro.observe(n); ro.observe(initial); }
      else window.addEventListener('resize', rebuild);
      /* logo/image tickers have zero width until their images arrive */
      n.querySelectorAll('img').forEach(function (img) { if (!img.complete) img.addEventListener('load', rebuild); });
      window.addEventListener('load', rebuild);
    });
  }

  /* ---------- Accordions ---------- */
  function measureAccordionItem(item) {
    var content = item.querySelector(':scope > [data-instant-type="accordion-content"]');
    if (!content) return;
    content.style.setProperty('--instant-accordion-content-height', content.scrollHeight + 'px');
  }
  function initAccordions(root) {
    root.querySelectorAll('[data-instant-type="accordion-container"]').forEach(function (acc) {
      if (acc.getAttribute('data-dg-accordion')) return;
      acc.setAttribute('data-dg-accordion', '1');
      var multi = acc.getAttribute('data-is-multi-open-enabled') === 'true';
      var firstOpen = acc.getAttribute('data-is-first-open') === 'true';
      var items = Array.prototype.slice.call(acc.querySelectorAll(':scope > [data-instant-type="accordion-item"]'));
      items.forEach(function (item, idx) {
        measureAccordionItem(item);
        if (firstOpen && idx === 0 && !item.getAttribute('data-state')) item.setAttribute('data-state', 'open');
        if (!item.getAttribute('data-state')) item.setAttribute('data-state', 'closed');
        var header = item.querySelector(':scope > [data-instant-type="accordion-header"]');
        if (!header) return;
        header.addEventListener('click', function (ev) {
          ev.preventDefault();
          var isOpen = item.getAttribute('data-state') === 'open';
          measureAccordionItem(item);
          item.setAttribute('data-state', isOpen ? 'closed' : 'open');
          if (!multi && !isOpen) items.forEach(function (o) { if (o !== item && o.getAttribute('data-state') === 'open') o.setAttribute('data-state', 'closed'); });
        });
      });
      var remeasure = debounce(function () { items.forEach(measureAccordionItem); }, 150);
      if ('ResizeObserver' in window) items.forEach(function (i) { var c = i.querySelector(':scope > [data-instant-type="accordion-content"]'); if (c && c.firstElementChild) new ResizeObserver(remeasure).observe(c.firstElementChild); });
      window.addEventListener('resize', remeasure);
      window.addEventListener('load', remeasure);
    });
  }

  /* ---------- Fill videos: autoplay + play/pause control ----------
     Autoplay videos play whenever they are on screen and pause when scrolled away (as Instant's
     runtime did). If something else pauses them (the theme's pauseAllMedia on gallery changes and
     modals, or the browser pausing off-screen/background video), they restart once back in view,
     unless the shopper paused them with the video's own control. */
  function initVideos(root) {
    root.querySelectorAll('.instant-video-fill__wrapper, .video-fill__wrapper').forEach(function (wrap) {
      if (wrap.getAttribute('data-dg-video')) return;
      wrap.setAttribute('data-dg-video', '1');
      var video = wrap.querySelector('video');
      if (!video) return;
      var userPaused = false;
      var inView = true;
      function tryPlay() {
        if (!video.autoplay || userPaused || !inView || document.visibilityState === 'hidden') return;
        video.muted = true;
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }
      if (video.autoplay) {
        tryPlay();
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            entries.forEach(function (en) {
              inView = en.isIntersecting;
              if (inView) tryPlay();
              else if (!video.paused) video.pause();
            });
          }, { threshold: 0.15 }).observe(wrap);
        }
        document.addEventListener('visibilitychange', tryPlay);
        var restarts = 0; // cap automatic restarts in case a browser keeps refusing playback
        video.addEventListener('pause', function () { if (restarts++ < 20) setTimeout(tryPlay, 150); });
      }
      var controls = wrap.querySelector('.instant-video-fill__controls, .video-fill__controls');
      if (controls) controls.addEventListener('click', function () {
        if (video.paused) { userPaused = false; video.play(); wrap.setAttribute('data-paused', 'false'); }
        else { userPaused = true; video.pause(); wrap.setAttribute('data-paused', 'true'); }
      });
    });
  }

  /* ---------- Scroll reveal ---------- */
  function initScrollTriggers(root) {
    var els = root.querySelectorAll('.instant-scroll-trigger');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(function (e) { e.classList.remove('instant-scroll-trigger--hidden'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var el = en.target, replay = el.getAttribute('data-instant-scroll-into-view-replay') === 'true';
        if (en.isIntersecting) { el.classList.remove('instant-scroll-trigger--hidden'); if (!replay) io.unobserve(el); }
        else if (replay) el.classList.add('instant-scroll-trigger--hidden');
      });
    }, { threshold: 0.1 });
    els.forEach(function (e) { io.observe(e); });
  }

  /* ---------- Clickable containers (data-instant-href) ---------- */
  function initHrefContainers(root) {
    root.querySelectorAll('[data-instant-href]').forEach(function (el) {
      if (el.getAttribute('data-dg-href')) return;
      el.setAttribute('data-dg-href', '1');
      el.addEventListener('click', function (ev) {
        if (ev.target.closest('a, button, input, select, textarea')) return;
        var href = el.getAttribute('data-instant-href');
        if (href) window.location.href = href;
      });
    });
  }

  function init(root) {
    root = root || document;
    initSliders(root); initTickers(root); initAccordions(root);
    initVideos(root); initScrollTriggers(root); initHrefContainers(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target || document); });
  window.DGRuntime = { init: init };
})();
